import React, { Ref } from 'react';
import ReactMarkdown from 'react-markdown';
import mergeRefs from 'react-merge-refs';

import { ButtonToggles, RadioButtonPanels, RadioButtons as EikaRadioButtons } from '@eika/radio-button';

import { FocusableHtmlElement, useFocusRef } from '../../sporsmalsflyt/hooks';
import { RadioType } from '../../types/generated-types';

import './radio-buttons.scss';

export const radioErrorMessage = (showFeilmelding: boolean, svarId?: string): string | undefined => {
    if (showFeilmelding && !svarId) {
        return 'Du må velge et alternativ';
    }
    return undefined;
};

export interface OptionProps {
    label: string;
    description?: string;
    value: string;
    id?: string;
    'data-track-id'?: string;
}

interface Props {
    name: string;
    label: string;
    info?: string;
    ingress?: string;
    options: OptionProps[];
    onChange: (eventValue: string) => void;
    selectedOption?: string;
    errorMessage?: string;
    focusOnMount: boolean;
    errorRef?: Ref<FocusableHtmlElement>;
    radioType?: RadioType;
}

export const RadioButtons = ({
    name,
    label,
    info = null,
    ingress = null,
    options,
    onChange,
    selectedOption,
    errorMessage,
    radioType = RadioType.Panels,
    focusOnMount = true,
    errorRef,
}: Props) => {
    const optionRef = useFocusRef<HTMLInputElement>(focusOnMount);

    const optionShouldHaveFocus = (option: OptionProps, index: number) =>
        selectedOption === option.value || (!selectedOption && index === 0);

    const optionFocus = () => {
        return options.map((option, index) => {
            if (optionShouldHaveFocus(option, index)) {
                return { ...option, ref: mergeRefs([optionRef, errorRef]), 'data-track-id': option['data-track-id'] };
            }
            return option;
        });
    };

    return (
        <div className="container__standard-width">
            <div className="info-container">
                <ReactMarkdown>{info}</ReactMarkdown>
            </div>
            {radioType === RadioType.Panels && (
                <RadioButtonPanels
                    label={label}
                    sublabel={ingress}
                    options={optionFocus()}
                    selectedOption={selectedOption}
                    onChange={onChange}
                    name={name}
                    errorMessage={errorMessage}
                    required
                />
            )}
            {radioType === RadioType.Toggles && (
                <ButtonToggles
                    label={label}
                    sublabel={ingress}
                    options={optionFocus()}
                    selectedOption={selectedOption ?? ''}
                    onChange={onChange}
                    errorMessage={errorMessage}
                    required
                />
            )}
            {radioType === RadioType.Buttons && (
                <EikaRadioButtons
                    label={label}
                    sublabel={ingress}
                    options={optionFocus()}
                    selectedOption={selectedOption}
                    onChange={onChange}
                    name={name}
                    errorMessage={errorMessage}
                    required
                />
            )}
        </div>
    );
};
